// Formats et thème des graphiques du Dashboard YT (fr-FR, charte RMR).
import { formatNumber } from '@/utils/formatting'
import {
  RMR_CA, RMR_GREEN, RMR_INDIGO, RMR_INDIGO_DARK, RMR_ORANGE, RMR_RPM, RMR_SESSIONS,
  RMR_TEXT_MUTED, RMR_TEXT_SUBTLE,
} from '@/styles/tokens'

export const COLORS = {
  views: RMR_SESSIONS,
  revenue: RMR_CA,
  rpm: RMR_RPM,
  cpm: RMR_ORANGE,
  discover: RMR_INDIGO,
  published: RMR_GREEN,
  neutral: RMR_TEXT_SUBTLE,
  palette: [RMR_INDIGO, RMR_SESSIONS, RMR_CA, RMR_GREEN, RMR_RPM, RMR_ORANGE, RMR_INDIGO_DARK, '#94A3B8'],
}

const NBSP = ' '

export const fInt = (value) => formatNumber(Math.round(Number(value) || 0), { nbDecimals: 0 })
export const fCompact = (value) => formatNumber(Number(value) || 0, { compact: true, nbDecimals: 1 })
export const fBig = (value) => (Math.abs(Number(value) || 0) >= 1e7 ? fCompact(value) : fInt(value))
export const fEur = (value) => {
  const number = Number(value) || 0
  return Math.abs(number) >= 1000
    ? formatNumber(Math.round(number), { currency: true, nbDecimals: 0 })
    : formatNumber(number, { currency: true, nbDecimals: 2 })
}
export const fEur2 = (value) => formatNumber(Number(value) || 0, { currency: true, nbDecimals: 2 })
export const fEurAxis = (value) => (Math.abs(value) >= 1e4 ? `${fCompact(value)}${NBSP}€` : fEur(value))
// Les parts arrivent en ratio (0-1) de l'API.
export const fPct = (value, nbDecimals = 1) => formatNumber(Number(value) || 0, { percent: true, nbDecimals })
// `avg_percentage` arrive déjà en pourcentage (0-100).
export const fPct100 = (value, nbDecimals = 1) => `${formatNumber(Number(value) || 0, { nbDecimals })}${NBSP}%`
export const fDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${pad(minutes)}:${pad(rest)}`
}
export const fHours = (value) => `${fBig(value)}${NBSP}h`
export const fDecimal = (value, nbDecimals = 1) => formatNumber(Number(value) || 0, { nbDecimals })

const withPremier = (label) => label.replace(/(^|[^\d])1 (?=[^\d\s])/, '$11er ')
const toDate = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00`)

export const fDay = (iso) => withPremier(toDate(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }))
export const fDayLong = (iso) => withPremier(toDate(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }))
export const fDateTime = (iso) => {
  if (!iso) return ''
  const date = new Date(iso)
  return `${withPremier(date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }))} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
}
export const fMonth = (iso) => toDate(iso).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })

export const bucketLabel = (bucket, granularity) => {
  if (granularity === 'month') return fMonth(bucket.date)
  if (granularity === 'week') return `sem. du ${fDay(bucket.date_from || bucket.date)}`
  return fDay(bucket.date)
}
export const bucketTitle = (bucket, granularity) => {
  if (granularity === 'day') return fDayLong(bucket.date)
  if (bucket.date_from === bucket.date_to) return fDayLong(bucket.date_from)
  return `du ${fDayLong(bucket.date_from)} au ${fDayLong(bucket.date_to)}`
}
export const GRANULARITY_LABELS = { day: 'jour', week: 'semaine', month: 'mois' }

export const SOURCE_TYPE_LABELS = {
  DISCOVER: 'Google Discover',
  YT_SEARCH: 'Recherche YouTube',
  SUBSCRIBER: 'Accueil et abonnements',
  RELATED_VIDEO: 'Vidéos suggérées',
  EXT_URL: 'Sources externes',
  NO_LINK_OTHER: 'Direct ou inconnu',
  YT_OTHER_PAGE: 'Autres pages YouTube',
  PLAYLIST: 'Playlists',
  NOTIFICATION: 'Notifications',
  YT_CHANNEL: 'Pages de chaîne',
  END_SCREEN: 'Écrans de fin',
  ADVERTISING: 'Publicité',
  SHORTS: 'Shorts',
  SOUND_PAGE: 'Pages de son',
  HASHTAGS: 'Hashtags',
  ANNOTATION: 'Annotations',
  CAMPAIGN_CARD: 'Fiches de campagne',
  PROMOTED: 'Promotion',
  YT_PLAYLIST_PAGE: 'Pages de playlist',
  VIDEO_REMIXES: 'Remix',
  SHORTS_CONTENT_LINKS: 'Liens depuis des Shorts',
  LIVE_REDIRECT: 'Redirections de direct',
  IMMERSIVE_LIVE: 'Direct immersif',
  PRODUCT_PAGE: 'Pages produit',
}
export const sourceTypeLabel = (type) => SOURCE_TYPE_LABELS[type] || type

export const baseChartOptions = () => ({
  chart: { toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif', zoom: { enabled: false }, animations: { enabled: false } },
  grid: { borderColor: '#F1F5F9', strokeDashArray: 4, xaxis: { lines: { show: false } } },
  dataLabels: { enabled: false },
  legend: { show: false },
  xaxis: {
    labels: { style: { colors: RMR_TEXT_SUBTLE, fontSize: '10px', fontWeight: 600 }, rotate: -45, hideOverlappingLabels: true },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: { labels: { style: { colors: RMR_TEXT_SUBTLE, fontSize: '10px', fontWeight: 600 } } },
  tooltip: { shared: true, intersect: false },
})

export const axisLabel = (text, color) => ({ text, style: { color, fontWeight: 700, fontSize: '11px' } })

export const sparklineOptions = (color, formatter = fInt) => ({
  chart: { type: 'area', sparkline: { enabled: true }, animations: { enabled: false } },
  colors: [color],
  stroke: { width: 2, curve: 'smooth' },
  fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
  tooltip: { fixed: { enabled: false }, x: { show: true }, y: { formatter: (v) => formatter(v), title: { formatter: () => '' } }, marker: { show: false } },
})

export const mutedText = RMR_TEXT_MUTED
